/**
 * PredictiveSearch + Feldsuche (Primo Vero)
 *
 * Dropdown beim Tippen (Shopify Predictive Search) mit Fallback-Kette:
 *   0. «se:kit» ohne Leerzeichen als Phrase; Doppelpunkt-Woerter in Mehrwort-Eingaben werden
 *      immer als Phrase gesendet (pvPredictiveQuery)
 *   1. Original, 2. Umlaut-Schreibweise, 2.5 Singular, 2.6 Zusammenzug («se motion» -> se:motion),
 *   3. Atom-Erweiterung («hive» -> se:hive), 4. Tippfehler-Korrektur gegen den Such-Index,
 *   5. leerer Zustand. Jede Stufe greift nur bei 0 Produkttreffern.
 *
 * Absenden (Enter, «Alle Ergebnisse», Such-Chips): Feldsuche v5 (pvDecideSmartQuery), siehe
 * Beschreibung im Block «Feldsuche». Die Ergebnisseite bekommt die interne Abfrage in q und den
 * Text fuer den Kunden in pv_anzeige.
 *
 * Such-Index: ein schlanker Abruf (templates/collection.pvindex.liquid), beim ersten Fokus ins
 * Suchfeld vorgeladen und pro Sitzung gespeichert. Ersetzt /products.json (4,8 MB).
 */

// --- PV: Fuzzy-Helpers ---------------------------------------------------

const PV_GERMAN_TRANSLITERATIONS = [
  [/ue/g, 'ü'],
  [/oe/g, 'ö'],
  [/ae/g, 'ä'],
  [/Ue/g, 'Ü'],
  [/Oe/g, 'Ö'],
  [/Ae/g, 'Ä'],
  [/ss/g, 'ß'],
];

function pvGetGermanVariant(query) {
  let variant = query;
  for (const [pattern, replacement] of PV_GERMAN_TRANSLITERATIONS) {
    variant = variant.replace(pattern, replacement);
  }
  return variant !== query ? variant : null;
}

function pvNormalize(s) {
  return (s || '')
    .toLowerCase()
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ß/g, 'ss');
}

// --- PV: Feldsuche (v5, 11.10.2026, Such-Audit Paket 1) -------------------------
// Befund 11.10.2026: Shopify wertet Suchen in Anfuehrungszeichen ("bürostuhl") nur noch in
// Titel und Beschreibung aus, nicht mehr in Marke, Produktart und Schlagwoertern. Dieselbe
// Abfrage wie im Such-Audit vom 21.08. lieferte am 11.10.: "narbutas" 105 -> 8,
// "impact" "acoustic" 91 -> 2, "bürostuhl" 46 -> 14 (Katalog unveraendert, per Dump belegt).
// Die bisherige Logik (v2 bis v4: Phrase je Wort, Kandidaten ueber suggest.json gezaehlt) baute
// genau auf das alte Verhalten und verlor dadurch die meisten Produkte; das Dropdown zeigte sie
// weiter, weil es eine andere Suchmaschine ist. Sicherung der alten Fassung:
// _suche_audit/paket1/basis_1013/assets/search.js (= Live NewHype_1_10.12).
//
// Neu fragt die Suche je Wort alle Felder ab, die Shopify auf der Ergebnisseite versteht:
//   ("wort" OR product_type:wort OR tag:wort OR vendor:wort OR product_type:"Pendelleuchte" ...)
// Mehrere Woerter = mehrere Gruppen (UND). Jedes Wort wird erweitert, alles aus Daten:
//   1. Umlaut-Schreibweise und Singular, der Singular nur, wenn es das Wort im Katalog gibt
//      («stühle» -> stuhl, «buerostuhl» -> bürostuhl);
//   2. Synonyme aus der Theme-Einstellung pv_search_synonyms, auch mit mehreren Zielen
//      («telefonkabine => telefonbox, meetingbox»); das getippte Wort bleibt immer dabei;
//   3. Grundwort -> Produktarten: Shopify zerlegt zusammengesetzte Produktarten nicht
//      (product_type:leuchte findet «Pendelleuchte» nicht). Jedes Grundwort zieht darum alle
//      Produktarten nach, die darauf enden. Die Zuordnung entsteht automatisch aus dem
//      Katalog (Such-Index, s. pvLoadIndex, und pvTypenZuWort); Ausnahmen mit Beleg in PV_KOPF_AUSNAHMEN.
// Gemessen am 11.10. (Prototyp gegen die Live-Suche): «bürostuhl» 11 -> 41 von 46 Bürostühlen,
// «narbutas» 8 -> 109 von 109, «lampe» (mit Synonym) 1 -> 46 von 123 Leuchten.
// Grenze: Shopifys Tippfehler-Toleranz greift bei Suchsyntax nicht. Darum wird jede Abfrage vor
// dem Absenden gezaehlt (view=pvcount). Bei 0 Treffern folgen Zusammenzug («se motion» ->
// se:motion), Atom-Erweiterung («hive» -> se:hive), Tippfehler-Korrektur, Kernwoerter, ODER und
// zuletzt die unveraenderte Eingabe, die Shopify mit eigener Tippfehler-Toleranz sucht.

// Expertensuche (Anfuehrungszeichen, Operatoren, Feldsyntax) bleibt unangetastet. Klammern gelten nicht
// als Syntax: sie stammen fast immer aus kopierten Produktnamen («MY 250 mm (nur für Reling)»).
// Der Doppelpunkt allein ist KEINE Expertensuche mehr: «se:cube S» wird zerlegt und das
// Doppelpunkt-Wort als Phrase gesucht (vorher: unveraendert gesendet = 1'000 Treffer).
const PV_RAW_QUERY_RE = /["*]|\b(AND|OR|NOT)\b|(^|\s)-|\b(product_type|tag|vendor|title|body|variants\.[a-z_]+):/;

function pvIsRawQuery(q) {
  if (!q) return false;
  const t = q.trim();
  return t.length >= 2 && t.length <= 80 && !PV_RAW_QUERY_RE.test(t);
}

function pvQuotePhrase(q) {
  return q
    .trim()
    .split(/\s+/)
    .map((w) => '"' + w + '"')
    .join(' ');
}

// Letzten Umlaut ins Grundgraphem zurückführen (Plural-Umlaut):
// "bürostühl" -> "bürostuhl" (das ü in "büro" bleibt unangetastet).
function pvUnumlautLast(w) {
  const map = { ä: 'a', ö: 'o', ü: 'u' };
  for (let i = w.length - 1; i >= 0; i--) {
    if (map[w[i]]) return w.slice(0, i) + map[w[i]] + w.slice(i + 1);
  }
  return null;
}

// Deutsche Singular-Heuristik: Plural-Endungen strippen (+ Umlaut-Rückführung).
// Regeln von "wenig abschneiden" (natürlichster Singular) zu "viel"; innerhalb jeder Regel
// zuerst die Umlaut-Rückführung (schränk->schrank, stühl->stuhl), dann die rohe Basis.
// Die Feldsuche nimmt nur Kandidaten, die es im Katalog gibt (Fantasieformen fallen weg).
function pvSingularCandidates(word) {
  const w = (word || '').toLowerCase();
  const out = [];
  const seen = new Set([w]);
  const push = (v) => {
    if (v && v.length >= 3 && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  };
  for (const suf of ['s', 'n', 'e', 'en', 'er']) {
    if (w.endsWith(suf) && w.length - suf.length >= 3) {
      const base = w.slice(0, w.length - suf.length);
      push(pvUnumlautLast(base));
      push(base);
    }
  }
  return out.slice(0, 5);
}

// Grundform-Kandidaten fürs Dropdown (Stufe 2.5). Im Deutschen steht der Kopf eines
// Kompositums/einer Phrase HINTEN ("grosse Tische" -> Kopf "Tische").
function pvMorphQueries(query) {
  const words = (query || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const cands = [];
  const add = (s) => {
    const t = (s || '').trim();
    if (t && cands.indexOf(t) === -1) cands.push(t);
  };
  const baseOf = (w) => pvSingularCandidates(w)[0] || w.toLowerCase();
  if (words.length === 1) {
    for (const b of pvSingularCandidates(words[0])) add(b);
  } else {
    add(words.map(baseOf).join(' ')); // alle Wörter singular
    add(words.slice(0, -1).concat([baseOf(words[words.length - 1])]).join(' ')); // nur Kopf singular
  }
  const low = query.toLowerCase().trim();
  return cands.filter((c) => c !== low).slice(0, 3);
}

// Erstbuchstabe gross — nur für die Banner-Anzeige deutscher Nomen.
function pvTitle(s) {
  if (!s) return s;
  // Marken-/Compound-Token (mit Trenner, z.B. «se:motion») und Mehrwort-Anzeigen nicht anfassen.
  if (/[:._\/\-]/.test(s)) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Synonyme aus der Theme-Einstellung: «von => nach» oder «von => ziel1, ziel2».
// Ergebnis: Map von (Kleinschrift, einfache Leerzeichen) -> Liste der Ziele.
// Ein Schluessel mit Leerzeichen ist ein Phrase-Synonym und ersetzt die ganze Eingabe.
function pvParseSynonyms(raw) {
  const map = new Map();
  if (!raw) return map;
  for (const line of String(raw).split(/\r?\n/)) {
    const m = line.split(/=>|=/);
    if (m.length < 2) continue;
    const from = m[0].trim().toLowerCase().replace(/\s+/g, ' ');
    if (!from) continue;
    const ziele = map.get(from) || [];
    for (const z of m[m.length - 1].split(',')) {
      const t = z.trim();
      if (t && t.toLowerCase() !== from && ziele.indexOf(t) === -1) ziele.push(t);
    }
    if (ziele.length) map.set(from, ziele);
  }
  return map;
}

// Wort-Synonyme auf eine Eingabe anwenden (erstes Ziel) — nur noch fuer Altaufrufe.
function pvApplySynonyms(query, synMap) {
  if (!synMap || synMap.size === 0) return { query: query, changed: false };
  let changed = false;
  const out = query
    .trim()
    .split(/\s+/)
    .map((w) => {
      const r = synMap.get(w.toLowerCase());
      if (r && r.length) {
        changed = true;
        return r[0];
      }
      return w;
    });
  return { query: out.join(' '), changed: changed };
}

// Echte Trefferzahl der /search-Seite über das schlanke Zähl-Template (templates/search.pvcount),
// mit denselben Parametern wie das Suchformular. Antwort ist eine nackte Zahl (wenige Bytes).
// Fehlt das Template, liefert Shopify HTML -> Längen-Riegel -> null («nicht messbar»).
var PV_ZAEHL_FRIST_MS = 1500;
var pvZaehlCache = new Map();
async function pvEchteTrefferzahl(query) {
  if (!query) return null;
  if (pvZaehlCache.has(query)) return pvZaehlCache.get(query);
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const uhr = setTimeout(() => ctrl && ctrl.abort(), PV_ZAEHL_FRIST_MS);
  try {
    const root = (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    const res = await fetch(
      `${base}/search?q=${encodeURIComponent(query)}&type=product%2Cpage&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last&view=pvcount`,
      ctrl ? { signal: ctrl.signal } : undefined
    );
    if (!res.ok) return null;
    const text = (await res.text()).trim();
    if (text.length > 12) return null; // keine nackte Zahl -> Template fehlt
    const n = parseInt(text, 10);
    if (!Number.isFinite(n)) return null;
    pvZaehlCache.set(query, n);
    return n;
  } catch (e) {
    return null;
  } finally {
    clearTimeout(uhr);
  }
}

// Woerter, die in Mehrwort-Eingaben nichts eingrenzen («stuhl für grosse personen»).
const PV_STOPPWOERTER = new Set([
  'für', 'fuer', 'fürs', 'mit', 'ohne', 'im', 'in', 'ins', 'der', 'die', 'das', 'den', 'dem', 'des',
  'ein', 'eine', 'einen', 'einem', 'einer', 'und', 'oder', 'von', 'vom', 'zum', 'zur', 'aus', 'bei',
  'auf', 'am', 'an', 'als', 'wie', 'zu', 'pour', 'de', 'des', 'du', 'et', 'the', 'for', 'with', 'and',
  'of', 'per', 'con', 'il', 'e', 'a', 'cm', 'mm', 'm', 'kg', 'x',
]);
// Feldsyntax nur fuer «saubere» Woerter (Buchstaben, Ziffern, Bindestrich).
const PV_FELD_SICHER_RE = /^[\p{L}\p{N}][\p{L}\p{N}\-]*$/u;
// Masse und Zahlen («180», «160x80», «180cm») grenzen nur als Phrase ein und sind keine Kernwoerter.
const PV_ZAHL_RE = /^\d+([.,x×\/\-]\d+)*(cm|mm|m|kg|w|er)?$/i;
// So lange darf die Suche beim Absenden auf den Such-Index warten (sonst ohne Produktarten).
var PV_INDEX_FRIST_MS = 1200;

function pvIndexMitFrist(ms) {
  return Promise.race([
    pvLoadIndex().catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

// Produktarten, deren Wort auf das gesuchte Grundwort endet («leuchte» -> Pendelleuchte, Tischleuchte,
// «säule» -> Akustiksäule). Shopify zerlegt Produktarten nicht, darum werden sie einzeln genannt.
// Ab 4 Buchstaben (kuerzer nur, was in PV_KOPF_KURZ_ERLAUBT steht), Ausnahmen in PV_KOPF_AUSNAHMEN.
function pvTypenZuWort(n, typWoerter) {
  if (!typWoerter || !n || (n.length < 4 && !PV_KOPF_KURZ_ERLAUBT.has(n)) || PV_KOPF_AUSNAHMEN.has(n)) return null;
  const typen = new Set();
  for (const [wort, ts] of typWoerter) {
    if (wort.length > n.length && wort.endsWith(n)) for (const t of ts) typen.add(t);
  }
  return typen.size ? typen : null;
}

function pvSuchKontext(index, synMap) {
  const da = !!(index && !index.leer);
  return {
    syn: synMap || new Map(),
    hatIndex: da,
    typen: (n) => (da ? pvTypenZuWort(n, index.typWoerter) : null),
    bekannt: (n) => da && (index.vocab.has(n) || index.typWoerter.has(n)),
    // Kernwort = kommt im Katalog vor (Titel, Produktart, Marke, Tag). Beim Lockern bleiben nur sie.
    kern: (n) => da && (index.vocab.has(n) || index.typWoerter.has(n) || index.markenWoerter.has(n)),
  };
}

function pvWoerter(text) {
  const roh = String(text || '')
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[,;!?«»'„“()[\]|]+|[,;!?«»'„“.()[\]|]+$/g, ''))
    .filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (roh.length < 2) return roh;
  const ohne = roh.filter((w) => !PV_STOPPWOERTER.has(w.toLowerCase()));
  return ohne.length ? ohne : roh;
}

// Eine Wortgruppe: alle Schreibweisen x alle Felder, dazu die Produktarten zum Grundwort.
function pvFeldGruppe(wort, ctx) {
  const w = String(wort).toLowerCase();
  // Marke-Modell («se:motion», «se:cube»): exakte Phrase (steht im Titel).
  if (w.indexOf(':') !== -1) return { wort: wort, q: '"' + w.replace(/"/g, '') + '"', kern: true };
  // Zahlen, Masse und Artikelnummern: als Phrase UND als Artikelnummer. Eine Phrase allein findet
  // Artikelnummern nicht mehr (gemessen 11.10.: "1.157.100010" 0, variants.sku:1.157.100010 1).
  if (PV_ZAHL_RE.test(w) || /\d[.\/]\d/.test(w)) {
    const sku = /^[\p{L}\p{N}.\-\/]+$/u.test(w) ? ' OR variants.sku:' + w : '';
    return { wort: wort, q: '("' + w.replace(/"/g, '') + '"' + sku + ')', kern: !PV_ZAHL_RE.test(w), zahl: PV_ZAHL_RE.test(w) };
  }

  const alts = [w];
  const dazu = (a) => {
    if (a && alts.indexOf(a) === -1) alts.push(a);
  };
  const tl = pvGetGermanVariant(w);
  if (tl) dazu(tl.toLowerCase());
  for (const a of alts.slice()) {
    for (const s of pvSingularCandidates(a)) {
      if (!ctx.hatIndex || ctx.bekannt(pvNormalize(s)) || ctx.syn.has(s)) {
        dazu(s);
        break;
      }
    }
  }
  for (const a of alts.slice()) {
    const ziele = ctx.syn.get(a);
    if (ziele) for (const z of ziele) dazu(z.toLowerCase());
  }

  const teile = [];
  let kern = false;
  for (const a of alts) {
    teile.push('"' + a.replace(/"/g, '') + '"');
    if (PV_FELD_SICHER_RE.test(a)) teile.push('product_type:' + a, 'tag:' + a, 'vendor:' + a);
    else if (/\s/.test(a)) teile.push('product_type:"' + a.replace(/"/g, '') + '"');
    const n = pvNormalize(a);
    const typen = ctx.typen(n);
    if (typen) for (const t of typen) teile.push('product_type:"' + String(t).replace(/"/g, '') + '"');
    if (typen || ctx.kern(n) || ctx.syn.has(a)) kern = true;
  }
  return { wort: wort, q: '(' + Array.from(new Set(teile)).join(' OR ') + ')', kern: kern };
}

// Getrennt geschriebene Woerter («büro stuhl», «se motion», «sitz steh tisch», «tisch lampe»):
// benachbarte Woerter mit den Trennern des Katalogs verbinden, wenn genau dieses Token im Katalog
// steht oder das zusammengeschriebene Wort ein Synonym-Schluessel ist. Drei Woerter vor zwei;
// Rueckgabe = neue Wortliste oder null.
function pvVerbindeWoerter(woerter, vocab, syn) {
  if (!vocab || !vocab.size || woerter.length < 2) return null;
  const treffer = (teile) => {
    if (teile.some((t) => PV_ZAHL_RE.test(t))) return null;
    for (const sep of PV_JOIN_SEPS) {
      const n = pvNormalize(teile.join(sep));
      if (vocab.has(n)) return vocab.get(n);
    }
    const zusammen = teile.join('').toLowerCase();
    return syn && syn.has(zusammen) ? zusammen : null;
  };
  const aus = [];
  let geaendert = false;
  for (let i = 0; i < woerter.length; ) {
    const drei = i + 2 < woerter.length ? treffer(woerter.slice(i, i + 3)) : null;
    const zwei = drei ? null : i + 1 < woerter.length ? treffer(woerter.slice(i, i + 2)) : null;
    if (drei || zwei) {
      aus.push(drei || zwei);
      i += drei ? 3 : 2;
      geaendert = true;
    } else {
      aus.push(woerter[i]);
      i += 1;
    }
  }
  return geaendert ? aus : null;
}

// Tippfehler in Woertern, die Kunden wirklich eingeben (Synonym-Schluessel wie «akustikbilder»,
// «lampe», «couch»): diese Woerter stehen nicht im Katalog, die Katalog-Korrektur findet sie nie.
function pvSynonymKorrektur(text, syn) {
  if (!syn || !syn.size) return null;
  const schluessel = Array.from(syn.keys()).filter((k) => k.length >= 5 && k.indexOf(' ') === -1);
  let geaendert = false;
  const aus = pvWoerter(text).map((w) => {
    const n = w.toLowerCase();
    if (n.length < 5 || syn.has(n) || /\d/.test(n)) return w;
    const max = n.length <= 7 ? 1 : 2;
    let best = null;
    let bestD = max + 1;
    for (const k of schluessel) {
      if (Math.abs(k.length - n.length) > max) continue;
      const d = pvDamerauLevenshtein(n, k, max);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    if (!best) return w;
    geaendert = true;
    return best;
  });
  return geaendert ? aus.join(' ') : null;
}

// Entscheid fuer das Absenden. Rueckgabe { query, display, changed, geprueft } oder null
// (= Eingabe unveraendert senden). display = was der Kunde im Suchfeld und im Hinweis sieht.
async function pvDecideSmartQuery(original, synMap) {
  const raw = String(original || '').trim().replace(/\s+/g, ' ');
  if (!pvIsRawQuery(raw)) return null;

  const index = await pvIndexMitFrist(PV_INDEX_FRIST_MS);
  const ctx = pvSuchKontext(index, synMap);

  // Phrase-Synonym: die ganze Eingabe («impact akustik => impact acoustic»).
  let basis = raw;
  const phrase = /\s/.test(raw) ? ctx.syn.get(raw.toLowerCase()) : null;
  if (phrase && phrase.length) basis = phrase[0];

  const woerter = pvWoerter(basis);
  if (!woerter.length) return null;
  const bauen = (liste) => liste.map((x) => pvFeldGruppe(x, ctx));
  const gruppen = bauen(woerter);
  const q1 = gruppen.map((g) => g.q).join(' ');
  const umgeschrieben = basis.toLowerCase() !== raw.toLowerCase();

  // Getrennt geschrieben, im Katalog verbunden («büro stuhl» -> «Bürostuhl»): die verbundene Form
  // hat Vorrang, sofern sie Treffer hat. Gezaehlt wird parallel zur Eingabe, also ohne Wartezeit.
  // Ein seltenes Wort verdraengt keine viel breitere Suche («steh sitz tisch»: «sitztisch» 2 statt 10);
  // Modellnamen mit Trenner («se:motion», «open-up») gelten immer.
  const verbunden = ctx.hatIndex ? pvVerbindeWoerter(woerter, index.vocab, ctx.syn) : null;
  const qv = verbunden ? bauen(verbunden).map((g) => g.q).join(' ') : null;
  const [n1, nv] = await Promise.all([pvEchteTrefferzahl(q1), qv ? pvEchteTrefferzahl(qv) : null]);
  const mitTrenner = verbunden && verbunden.some((w) => /[:\-]/.test(w) && woerter.indexOf(w) === -1);
  if (nv > 0 && (mitTrenner || n1 === null || nv * 4 >= n1)) {
    return { query: qv, display: verbunden.join(' '), changed: true, geprueft: true };
  }
  // Nicht zaehlbar (Netz, Frist): Feldsuche trotzdem senden; die Leerseiten-Kaskade auf der
  // Ergebnisseite faengt den seltenen Fall ab, dass sie leer bleibt.
  if (n1 === null) return { query: q1, display: basis, changed: umgeschrieben, geprueft: false };

  const kernGruppen = gruppen.filter((g) => g.kern);
  const kernTeilmenge = gruppen.length >= 2 && kernGruppen.length && kernGruppen.length < gruppen.length;

  // Treffer gefunden: die genaue Suche gilt («sedus quarterback 2» = genau diese Modelle). Gelockert
  // wird nur, wenn sie leer bleibt (unten), z. B. «schreibtisch 180 cm» -> «schreibtisch».
  if (n1 > 0) return { query: q1, display: basis, changed: umgeschrieben, geprueft: true };

  // 0 Treffer: Varianten bauen, parallel zaehlen; die erste mit Treffern gewinnt (Reihenfolge = Vorrang).
  const texte = [];
  const merken = (t) => {
    if (t && t.toLowerCase() !== basis.toLowerCase() && texte.indexOf(t) === -1) texte.push(t);
  };
  try {
    for (const j of await pvJoinedCompoundCandidates(basis)) merken(j);
  } catch (e) {}
  try {
    merken(await pvExpandToCompound(basis));
  } catch (e) {}
  try {
    merken(await pvSpellCorrectQuery(basis));
  } catch (e) {}
  merken(pvSynonymKorrektur(basis, ctx.syn));
  const kandidaten = texte.map((t) => ({ q: bauen(pvWoerter(t)).map((g) => g.q).join(' '), display: t }));
  if (kernTeilmenge) {
    kandidaten.push({ q: kernGruppen.map((g) => g.q).join(' '), display: kernGruppen.map((g) => g.wort).join(' ') });
  }
  if (gruppen.length >= 2) {
    kandidaten.push({ q: gruppen.map((g) => g.q).join(' OR '), display: woerter.join(' oder ') });
  }
  if (!kandidaten.length) return null;
  const zahlen = await Promise.all(kandidaten.map((k) => pvEchteTrefferzahl(k.q)));
  for (let i = 0; i < kandidaten.length; i++) {
    if (zahlen[i] && zahlen[i] > 0) {
      return { query: kandidaten[i].q, display: kandidaten[i].display, changed: true, geprueft: true };
    }
  }
  return null; // unveraendert senden: Shopifys eigene Tippfehler-Toleranz
}

// Lesbare Anzeige aus einer Feldabfrage (fuer geteilte Links ohne pv_anzeige):
// je Gruppe das erste Wort in Anfuehrungszeichen.
function pvAnzeigeAusQuery(q) {
  const s = String(q || '');
  if (s.indexOf('product_type:') === -1) return s.replace(/"/g, '').replace(/\s+OR\s+/g, ' oder ');
  const teile = [];
  const re = /\(\s*"([^"]+)"/g;
  let m;
  while ((m = re.exec(s))) teile.push(m[1]);
  return teile.join(' ');
}

// Dropdown: Doppelpunkt-Woerter in Mehrwort-Eingaben als Phrase senden, sonst liest Shopify
// «se:lounge light» als Feldsyntax (gemessen 11.10.: Leuchten statt se:lounge).
function pvPredictiveQuery(q) {
  const t = String(q || '').trim();
  if (!/\s/.test(t) || t.indexOf(':') === -1 || t.indexOf('"') !== -1) return t;
  return t
    .split(/\s+/)
    .map((w) => (w.indexOf(':') !== -1 ? '"' + w + '"' : w))
    .join(' ');
}

function pvDamerauLevenshtein(a, b, maxEdits) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  if (Math.abs(a.length - b.length) > maxEdits) return maxEdits + 1;

  const m = a.length;
  const n = b.length;
  let prevPrev = null;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);

  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    let rowMin = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        curr[j] = Math.min(curr[j], prevPrev[j - 2] + 1);
      }
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > maxEdits) return maxEdits + 1;
    prevPrev = prev;
    prev = curr;
    curr = new Array(n + 1);
  }
  return prev[n];
}

// Such-Index (v5, 11.10.2026): EIN schlanker Abruf statt /products.json (vorher 5 Abrufe,
// 0,55 MB Übertragung, 4,8 MB Daten, bei JEDEM Absenden). Quelle ist die Vorlage
// templates/collection.pvindex.liquid: /collections/all?view=pvindex&page=N liefert je 250
// Produkte als [Titel, Marke, Produktart, Tags] und dazu alle Produktarten und Marken des Shops
// (rund 30 KB komprimiert für den ganzen Katalog). Der Index wird beim ersten Fokus ins Suchfeld
// vorgeladen und pro Sitzung in sessionStorage gehalten (6 h). Fehlt die Vorlage, bleibt er leer:
// Die Suche funktioniert dann ohne Produktarten-Erweiterung, Zusammenzug und Tippfehler-Korrektur.
// - vocab: normalisierter Token -> Originalschreibweise (Tippfehler-Korrektur, Singular-Pruefung)
// - compounds: Atom -> Set der Compounds, die es enthalten ("hive" -> {"se:hive"})
// - typWoerter: Wort aus einer Produktart -> Set der Produktarten
// - markenWoerter: Woerter aus Markennamen
// Grundwort -> Produktarten ermittelt pvTypenZuWort zur Laufzeit aus typWoerter.
let pvIndexPromise = null;

const PV_COMPOUND_SPLIT_RE = /[:_\/.\-]+/;
const PV_KEEP_CHARS_RE = /[^\p{L}\p{N}:_\/.\-]/gu;
const PV_INDEX_SPEICHER = 'pv-suchindex-v5';
const PV_INDEX_GUELTIG_MS = 6 * 3600 * 1000;
// Grundwoerter, die NICHT als Kopf von Produktarten gelten (pvTypenZuWort; Beleg am Katalog vom 11.10.2026):
//   tische  -> traefe das Adjektiv «akustische» (6 Produktarten «Akustische ...»)
//   raum    -> traefe «Schreibtisch mit Stauraum»
//   element -> traefe «Schreibtischaufsatzelement»
const PV_KOPF_AUSNAHMEN = new Set(['tische', 'raum', 'element']);
// Grundwoerter unter 4 Buchstaben nur, wenn ausdruecklich erlaubt (sonst «and», «eck» ...).
const PV_KOPF_KURZ_ERLAUBT = new Set(['box']);

async function pvIndexRohdaten() {
  try {
    const s = sessionStorage.getItem(PV_INDEX_SPEICHER);
    if (s) {
      const o = JSON.parse(s);
      if (o && o.d && o.t && Date.now() - o.t < PV_INDEX_GUELTIG_MS) return o.d;
    }
  } catch (e) {
    // kein sessionStorage (privates Fenster, Vorschau) -> frisch laden
  }
  const root = (typeof FoxTheme !== 'undefined' && FoxTheme.routes && FoxTheme.routes.root_url) || '/';
  const base = root.endsWith('/') ? root : `${root}/`;
  const seite = async (n) => {
    const res = await fetch(`${base}collections/all?view=pvindex&page=${n}`);
    if (!res.ok) throw new Error(`pvindex ${res.status}`);
    return JSON.parse(await res.text());
  };
  const erste = await seite(1);
  const d = { typen: erste.typen || [], marken: erste.marken || [], p: (erste.p || []).slice() };
  const seiten = Math.min(parseInt(erste.seiten, 10) || 1, 20);
  if (seiten > 1) {
    const rest = await Promise.all(Array.from({ length: seiten - 1 }, (_, i) => seite(i + 2)));
    for (const r of rest) for (const zeile of r.p || []) d.p.push(zeile);
  }
  try {
    sessionStorage.setItem(PV_INDEX_SPEICHER, JSON.stringify({ t: Date.now(), d: d }));
  } catch (e) {
    // voll oder gesperrt: dann eben ohne Zwischenspeicher
  }
  return d;
}

function pvLoadIndex() {
  if (pvIndexPromise) return pvIndexPromise;

  pvIndexPromise = (async () => {
    const vocab = new Map();
    const compounds = new Map();
    const typWoerter = new Map();
    const markenWoerter = new Set();

    let d = null;
    try {
      d = await pvIndexRohdaten();
    } catch (e) {
      d = null;
    }
    if (!d) return { vocab, compounds, typWoerter, markenWoerter, leer: true };

    const addVocabEntry = (cleaned) => {
      if (!cleaned || cleaned.length < 3) return;
      const norm = pvNormalize(cleaned);
      if (!vocab.has(norm)) vocab.set(norm, cleaned);
    };
    const addCompoundLink = (atom, compoundOriginal) => {
      const norm = pvNormalize(atom);
      let set = compounds.get(norm);
      if (!set) {
        set = new Set();
        compounds.set(norm, set);
      }
      set.add(compoundOriginal);
    };

    for (const zeile of d.p) {
      const [titel, marke, typ, tags] = zeile;
      const text = [titel, marke, typ, (tags || []).join(' ')].filter(Boolean).join(' ');
      for (const raw of text.split(/\s+/)) {
        const cleaned = raw.replace(PV_KEEP_CHARS_RE, '');
        if (cleaned.length < 2) continue;
        // 1) Den ganzen "Wortbrocken" (inkl. Compound-Trenner wie ":" oder "-") als Vokabel.
        addVocabEntry(cleaned);
        // 2) Compound-Trenner: Atome separat indexieren UND Atom->Compound merken.
        if (PV_COMPOUND_SPLIT_RE.test(cleaned)) {
          for (const atom of cleaned.split(PV_COMPOUND_SPLIT_RE).filter((a) => a.length >= 3)) {
            addVocabEntry(atom);
            addCompoundLink(atom, cleaned);
          }
        }
      }
    }

    for (const typ of d.typen) {
      for (const w of String(typ).split(/[\s\-\/]+/)) {
        const n = pvNormalize(w);
        if (n.length < 3) continue;
        if (!typWoerter.has(n)) typWoerter.set(n, new Set());
        typWoerter.get(n).add(typ);
      }
    }
    for (const m of d.marken) {
      for (const w of String(m).split(/[\s\-\/.]+/)) {
        const n = pvNormalize(w);
        if (n.length >= 2) markenWoerter.add(n);
      }
    }

    return { vocab, compounds, typWoerter, markenWoerter, leer: false };
  })();

  return pvIndexPromise;
}

async function pvLoadVocabulary() {
  return (await pvLoadIndex()).vocab;
}

async function pvLoadCompounds() {
  return (await pvLoadIndex()).compounds;
}

// Versucht jeden Query-Begriff durch ein bekanntes Compound zu ersetzen, in dem er als Atom vorkommt.
// "hive" -> "se:hive", "ofy" -> "se:ofy", "konferenz" bleibt unverändert (falls als Vollwort vorhanden).
// Gibt null zurück wenn keine Ersetzung gemacht wurde.
async function pvExpandToCompound(query) {
  const compounds = await pvLoadCompounds();
  if (compounds.size === 0) return null;

  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  let expanded = false;
  const result = words.map((w) => {
    const norm = pvNormalize(w);
    if (norm.length < 3) return w;
    const set = compounds.get(norm);
    if (!set || set.size === 0) return w;
    // Erstes bekanntes Compound zurückgeben (deterministisch via Set-Insertion-Order).
    expanded = true;
    return set.values().next().value;
  });

  return expanded ? result.join(' ') : null;
}

// v4: Compound-Token-REKONSTRUKTION (Gegenstück zu pvExpandToCompound). Shopify
// indexiert «se:motion» als EIN Doppelpunkt-Token; getippt «se motion»
// (Leerzeichen) erreicht es nie. Standard-IR-Query-Konkatenation: benachbarte
// Query-Wörter mit den Trennern zusammenfügen, die der Katalog wirklich nutzt,
// und JEDEN Kandidaten EXAKT gegen das Live-Vokabular prüfen. Kein Wortlisten-
// Pflege; existiert das Token nicht, fällt der Kandidat durch. Geprüft wird stets
// der zusammengefügte String (nie «se» allein -> der 2-Zeichen-Atom-Floor bleibt
// unangetastet, denn das volle Token «se:motion» IST im Vokabular).
const PV_JOIN_SEPS = [':', '-', '.', '/', '_', ''];

async function pvJoinedCompoundCandidates(query) {
  const words = (query || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [];
  let vocab;
  try {
    vocab = await pvLoadVocabulary();
  } catch (e) {
    return [];
  }
  if (!vocab || !vocab.size) return [];

  const results = [];
  const seen = new Set([(query || '').toLowerCase().trim()]);
  const push = (phrase) => {
    const key = (phrase || '').toLowerCase().trim();
    if (key && !seen.has(key)) {
      seen.add(key);
      results.push(phrase);
    }
  };

  // Zwei benachbarte Wörter zu einem echten Katalog-Token verschmelzen (Trenner
  // in Reihenfolge: Doppelpunkt/Bindestrich zuerst, leerer Join zuletzt).
  const joinHit = (a, b) => {
    for (const sep of PV_JOIN_SEPS) {
      const norm = pvNormalize(a + sep + b);
      if (vocab.has(norm)) return vocab.get(norm); // Originalschreibweise des Tokens
    }
    return null;
  };

  // Greedy links->rechts: «se motion net» -> «se:motion net» (erstes Paar ist
  // echtes Token, «net» bleibt eigenständiges Wort).
  const merged = [];
  let i = 0;
  let didMerge = false;
  while (i < words.length) {
    const hit = i + 1 < words.length ? joinHit(words[i], words[i + 1]) : null;
    if (hit) {
      merged.push(hit);
      i += 2;
      didMerge = true;
    } else {
      merged.push(words[i]);
      i += 1;
    }
  }
  if (didMerge) push(merged.join(' '));

  // Genau 2 Wörter: zusätzlich jede einzelne Trenner-Variante als eigener
  // Kandidat (die Live-Zählung im Submit wählt die trefferstärkste Form).
  if (words.length === 2) {
    for (const sep of PV_JOIN_SEPS) {
      const norm = pvNormalize(words[0] + sep + words[1]);
      if (vocab.has(norm)) push(vocab.get(norm));
    }
  }

  return results.slice(0, 4);
}

async function pvSpellCorrectQuery(query) {
  const vocab = await pvLoadVocabulary();
  if (vocab.size === 0) return null;

  const words = query.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  let corrected = false;
  const fixed = words.map((w) => {
    const normW = pvNormalize(w);
    if (normW.length < 3) return w;
    if (vocab.has(normW)) return vocab.get(normW);

    let bestNorm = null;
    let bestDist = Infinity;
    const maxEdits = normW.length <= 4 ? 1 : 2;

    for (const v of vocab.keys()) {
      if (Math.abs(v.length - normW.length) > maxEdits) continue;
      const d = pvDamerauLevenshtein(normW, v, maxEdits);
      if (d < bestDist) {
        bestDist = d;
        bestNorm = v;
        if (d === 0) break;
      }
    }

    if (bestNorm && bestDist <= maxEdits) {
      corrected = true;
      return vocab.get(bestNorm);
    }
    return w;
  });

  return corrected ? fixed.join(' ') : null;
}

function pvResponseHasResults(responseText, sectionId) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(responseText, 'text/html');
    const container = doc.getElementById(`PredictiveSearchResults-${sectionId}`);
    if (!container) return true; // unbekannt -> rendern lassen
    if (container.querySelector('.empty-state')) return false;
    if (!container.innerHTML.trim()) return false;
    // v3: "hat Treffer" = hat PRODUKT-Treffer. Die Predictive-Section rendert
    // auch bei 0 Produkten Nicht-Produkt-Inhalt (Query-/Collection-Vorschläge),
    // hat also kein .empty-state — das galt bisher fälschlich als Treffer und
    // würgte die Morphologie-Fallback-Kette ab (Ursache «Leuchten» = 0 Produkte
    // im Dropdown, obwohl «Leuchte» 31 hat). Produkte stehen ausschliesslich im
    // Produkt-Block; Empfehlungen liegen ausserhalb dieses Containers.
    const hasProducts =
      !!container.querySelector('#predictive-search-results-products-list') ||
      !!container.querySelector('.predictive-search-result--products') ||
      !!container.querySelector('a[href*="/products/"]');
    return hasProducts;
  } catch (e) {
    return true;
  }
}

function pvEscapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function pvBuildCorrectionBannerHtml(correction, options = {}) {
  // options.actionLabel: Text fürs Trotzdem-Element
  // options.actionTag: 'button' (Predictive) oder 'a' (Search-Page)
  // options.actionHref: nur für 'a'
  const actionLabel = options.actionLabel || `Stattdessen nach „${correction.original}“ suchen`;
  const isLink = options.actionTag === 'a';
  const actionEl = isLink
    ? `<a class="pv-search-correction-banner__force-link" href="${pvEscapeHtml(options.actionHref || '#')}">${pvEscapeHtml(actionLabel)}</a>`
    : `<button type="button" class="pv-search-correction-banner__force-link">${pvEscapeHtml(actionLabel)}</button>`;

  return `
    <div class="pv-search-correction-banner" role="status">
      <span class="pv-search-correction-banner__text">
        Ergebnisse für <strong>${pvEscapeHtml(pvTitle(correction.corrected))}</strong>
      </span>
      ${actionEl}
    </div>
  `;
}

// --- Class ----------------------------------------------------------------

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this._flowId = 0;
    this._lastCorrection = null; // { original, corrected } – nur wenn der zuletzt gerenderte Treffer korrigiert war
    this._skipCorrectionOnce = false;

    // PV: Form-Submit abfangen, damit eine bereits ermittelte Korrektur an die /search-Seite mitgegeben wird
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    // PV v5: Rueckkehr aus dem bfcache -> Ladezeichen und Sperre loesen.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        this._pvSubmitting = false;
        this._pvEntscheidLaeuft = false;
        this.setLoadingState(false);
      }
    });

    // PV Paket 2: Klicks im Dropdown messen (Produkt, Kategorie, Seite), s. pvSuchMessen.
    this.addEventListener(
      'click',
      (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (!a || !a.closest('[id^="PredictiveSearchResults-"]')) return;
        const href = a.getAttribute('href') || '';
        const art = /\/products\//.test(href) ? 'produkt' : /\/collections\//.test(href) ? 'kategorie' : /\/pages\//.test(href) ? 'seite' : 'anderes';
        const karte = a.closest('#predictive-search-results-products-list > *');
        const position = karte ? Array.prototype.indexOf.call(karte.parentElement.children, karte) + 1 : 0;
        pvSuchMessen('pv_suche_dropdown', {
          suchbegriff: pvSuchbegriffSauber(this.getQuery()),
          art: art,
          position: position,
          ziel: href.split('?')[0].slice(0, 80),
        });
      },
      true
    );

    // PV v5: Such-Chips mit Suchziel (data-pv-chip) laufen durch denselben Entscheid wie Enter.
    this.querySelectorAll('a[data-pv-chip]').forEach((a) => {
      a.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        if (!form || this.dataset.pvSmart === 'false') return;
        e.preventDefault();
        this.input.value = a.dataset.pvChip;
        this._lastCorrection = null;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    });
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    // PV v5: Such-Index vorladen, damit er beim Absenden bereitliegt (ein Abruf, ~30 KB komprimiert).
    pvLoadIndex();
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );

    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      if (this.toggleNavigationButton) {
        this.toggleNavigationButton.classList.remove('is-show');
      }
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }

    this.classList.add('predictive-search-open');

    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }

    this.searchWithFallback(this.getQuery());
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange() {
    const selectedUrl = this.searchProductTypes?.value;

    if (selectedUrl) {
      window.location.href = selectedUrl;
      return;
    }

    const query = this.getQuery();
    if (query.length > 0) {
      this.searchWithFallback(query);
    }
  }

  setupURL() {
    return this.setupURLForQuery(this.getQuery());
  }

  setupURLForQuery(query) {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    url.searchParams.set('q', pvPredictiveQuery(query));

    // PV: Nur relevante Ressourcentypen abfragen. v5 (2026-07-15): 'query' raus —
    // die «Nach X suchen»-Query-Vorschlagszeilen sind Declutter-Noise (Produkte
    // werden bereits live gezeigt). v6 (2026-07-26): 'page' zurück — gemessen lieferten
    // architekten/konfigurator/lieferung/kontakt/faq vorher 0 Treffer. Rendering ist auf
    // max. 3 Zeilen in einer eigenen Gruppe «Beratung & Service» begrenzt.
    url.searchParams.set('resources[type]', 'product,collection,page');

    // PV: Anzahl Resultate
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 6);
    url.searchParams.set('resources[limit_scope]', 'each');

    // PV: Nicht verfügbare Produkte nicht komplett ausblenden, sondern hinten anzeigen
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    // PV: Suchfelder erweitern – wichtig: tag ergänzen
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag,variants.sku'
    );

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.searchWithFallback(this.getQuery());
  }

  // PV: 4-stufige Fallback-Kette für Typo-Toleranz
  async searchWithFallback(originalQuery) {
    this._flowId += 1;
    const myFlowId = this._flowId;
    const stillCurrent = () => myFlowId === this._flowId && this.getQuery() === originalQuery;

    // Wenn der User explizit "Trotzdem suchen" geklickt hat, Korrektur einmalig überspringen
    const skipCorrection = this._skipCorrectionOnce === true;
    this._skipCorrectionOnce = false;

    this.setLoadingState(true);

    try {
      // PV 0. Doppelpunkt-Query (23.09.2026): «se:kit» ohne Leerzeichen — Shopify liest den Doppelpunkt als
      // Feldsyntax, die rohe Query liefert 10 FREMDE Treffer (und damit griff nie eine Fallback-Stufe).
      // Zuerst die Phrase in Anfuehrungszeichen (exakt: «se:kit» -> 3 Treffer), bei 0 (Praefix «se:ki»
      // findet als Phrase nichts) die Variante mit Leerzeichen («se ki»), erst dann der bisherige Weg.
      let result;
      if (/:/.test(originalQuery) && !/\s/.test(originalQuery) && !/["']/.test(originalQuery) && originalQuery.length <= 40) {
        for (const cand of ['"' + originalQuery + '"', originalQuery.replace(/:+/g, ' ').trim()]) {
          if (!cand || cand === originalQuery || !stillCurrent()) continue;
          result = await this.fetchQueryResponse(cand);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = null;   // gleiche Absicht, nur anders geschrieben: kein Korrekturhinweis
            this.renderResponseText(result.text);
            return;
          }
        }
      }
      // 1. Originale Query
      result = await this.fetchQueryResponse(originalQuery);
      if (!stillCurrent()) return;
      if (result.hasResults || skipCorrection) {
        this._lastCorrection = null;
        this.renderResponseText(result.text);
        return;
      }

      // 2. Deutsche Transliteration (ue->ü etc.)
      const germanVariant = pvGetGermanVariant(originalQuery);
      if (germanVariant && stillCurrent()) {
        result = await this.fetchQueryResponse(germanVariant);
        if (!stillCurrent()) return;
        if (result.hasResults) {
          this._lastCorrection = { original: originalQuery, corrected: germanVariant };
          this.renderResponseText(result.text, this._lastCorrection);
          return;
        }
      }

      // 2.5 Morphologie: Plural -> Singular (+ Umlaut-Rückführung). Shopifys
      // DE-Suche macht KEIN Stemming, «Leuchten» matcht sonst nichts, obwohl
      // «Leuchte» 31 Treffer hat. Generische Sprachregel -> greift auch bei
      // neuen Warengruppen. Lose Kandidaten sind ok: der Live-Index entscheidet,
      // Treffer-lose Formen fallen einfach durch. Max. 3 Zusatz-Requests, und nur
      // wenn Original + Transliteration schon 0 Treffer hatten.
      for (const cand of pvMorphQueries(originalQuery)) {
        if (!stillCurrent()) return;
        result = await this.fetchQueryResponse(cand);
        if (!stillCurrent()) return;
        if (result.hasResults) {
          this._lastCorrection = { original: originalQuery, corrected: cand };
          this.renderResponseText(result.text, this._lastCorrection);
          return;
        }
      }

      // 2.6 Compound-Token-Rekonstruktion: «se motion» -> «se:motion». Marken-
      // Modell-Namen mit Doppelpunkt (Sedus se:-Linie) sind EIN Katalog-Token;
      // getippte Leerzeichen dazwischen via echtem Vokabular-Token zusammenführen.
      // Läuft nur, nachdem Original + Transliteration + Morphologie 0 Produkt-
      // Treffer hatten (Guard: nur bei Null-Treffer korrigieren). Standard-IR-
      // Query-Konkatenation, gegen den Live-Katalog validiert.
      try {
        for (const cand of await pvJoinedCompoundCandidates(originalQuery)) {
          if (!stillCurrent()) return;
          result = await this.fetchQueryResponse(cand);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected: cand };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Vokabular-Load fehlgeschlagen -> Schritt überspringen
      }

      // 3. Compound-Expansion ("hive" -> "se:hive", "ofy" -> "se:ofy", ...)
      // Hilft bei Shopify-Tokenizer-Schwäche: das Atom existiert im Produktindex,
      // aber Shopify findet es nur über die volle Compound-Form.
      try {
        const expanded = await pvExpandToCompound(originalQuery);
        if (expanded && expanded !== originalQuery && stillCurrent()) {
          result = await this.fetchQueryResponse(expanded);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected: expanded };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Index-Load fehlgeschlagen -> Schritt überspringen
      }

      // 4. Fuzzy-Spellcorrection gegen Produkt-Vokabular
      try {
        const corrected = await pvSpellCorrectQuery(originalQuery);
        if (corrected && corrected !== originalQuery && stillCurrent()) {
          result = await this.fetchQueryResponse(corrected);
          if (!stillCurrent()) return;
          if (result.hasResults) {
            this._lastCorrection = { original: originalQuery, corrected };
            this.renderResponseText(result.text, this._lastCorrection);
            return;
          }
        }
      } catch (e) {
        // Vocab-Load fehlgeschlagen -> Fuzzy-Step überspringen
      }

      // 5. Empty-State des Originals rendern
      if (stillCurrent()) {
        const fallback = await this.fetchQueryResponse(originalQuery);
        if (stillCurrent()) {
          this._lastCorrection = null;
          this.renderResponseText(fallback.text);
        }
      }
    } catch (error) {
      console.error('Error fetching data: ', error);
      this.setAttribute('error', 'Failed to load data');
    } finally {
      if (myFlowId === this._flowId) {
        this.setLoadingState(false);
      }
    }
  }

  async fetchQueryResponse(query) {
    const url = this.setupURLForQuery(query).toString();
    let text;
    if (this.cachedMap.has(url)) {
      text = this.cachedMap.get(url);
    } else {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      text = await response.text();
      this.cachedMap.set(url, text);
    }
    const sectionId = FoxTheme.utils.getSectionId(this);
    return { text, hasResults: pvResponseHasResults(text, sectionId) };
  }

  renderResponseText(responseText, correction = null) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        // PV: "Meinten Sie X?"-Banner einsetzen, wenn die gerenderten Treffer aus einer Korrektur stammen
        if (correction && correction.original && correction.corrected) {
          this.injectCorrectionBanner(targetElement, correction);
        }

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavigationButton?.classList.add('is-show');
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  // PV: Banner über die Trefferliste setzen, mit klickbarem "Trotzdem nach 'X' suchen"-Element
  injectCorrectionBanner(container, correction) {
    if (container.querySelector('.pv-search-correction-banner')) return;

    const wrap = document.createElement('div');
    wrap.innerHTML = pvBuildCorrectionBannerHtml(correction);
    const banner = wrap.firstElementChild;
    if (!banner) return;

    const forceBtn = banner.querySelector('.pv-search-correction-banner__force-link');
    if (forceBtn) {
      forceBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.input.value = correction.original;
        this._skipCorrectionOnce = true;
        this.searchWithFallback(correction.original);
      });
    }

    container.insertBefore(banner, container.firstChild);
  }

  // PV: Form-Submit (v5, 11.10.2026).
  // 1. Hat das Dropdown korrigiert («buerostuhl» -> «bürostuhl»), gilt die Korrektur.
  // 2. pvDecideSmartQuery baut die Feldsuche und zaehlt sie (Block «Feldsuche» oben).
  // 3. Navigation direkt ueber die URL: das sichtbare Suchfeld behaelt den getippten Text (vorher
  //    stand dort kurz die interne Abfrage), die Ergebnisseite zeigt pv_anzeige statt der Syntax.
  // Waehrend der Entscheid laeuft, zeigt das Feld ein Ladezeichen; ein zweites Enter wird ignoriert.
  onFormSubmit(event) {
    if (this._pvSubmitting) return; // programmatischer Re-Submit (pvSmart=false)
    const form = event.target;
    const typedQuery = this.getQuery();

    let effectiveQuery = typedQuery;
    let bannerOriginal = null;
    if (
      this._lastCorrection &&
      this._lastCorrection.original === typedQuery &&
      this._lastCorrection.corrected &&
      this._lastCorrection.corrected !== typedQuery
    ) {
      effectiveQuery = this._lastCorrection.corrected;
      bannerOriginal = this._lastCorrection.original;
    }

    if (this.dataset.pvSmart === 'false' || !typedQuery) {
      if (bannerOriginal) {
        this.input.value = effectiveQuery;
        this._pvSetHiddenOriginal(form, bannerOriginal);
      }
      return; // native Submission
    }

    event.preventDefault();
    if (this._pvEntscheidLaeuft) return;
    this._pvEntscheidLaeuft = true;
    this.setLoadingState(true);

    const los = (decision) => {
      const params = new URLSearchParams();
      form.querySelectorAll('input[type="hidden"]').forEach((el) => {
        if (el.name && el.name !== 'q' && el.name.indexOf('pv_') !== 0) params.append(el.name, el.value);
      });
      const query = decision && decision.query ? decision.query : effectiveQuery;
      params.set('q', query);
      if (query !== effectiveQuery) params.set('pv_anzeige', (decision && decision.display) || effectiveQuery);
      if ((decision && decision.changed) || bannerOriginal) params.set('pv_corrected_from', bannerOriginal || typedQuery);
      this._pvSubmitting = true;
      const action = form.getAttribute('action') || '/search';
      window.location.href = action + (action.indexOf('?') === -1 ? '?' : '&') + params.toString();
    };

    const frist = new Promise((resolve) => setTimeout(() => resolve('frist'), 3500));
    Promise.race([pvDecideSmartQuery(effectiveQuery, this._pvGetSynonyms()), frist])
      .then((d) => los(d === 'frist' ? null : d))
      .catch(() => los(null));
  }

  _pvSetHiddenOriginal(form, value) {
    let hidden = form.querySelector('input[name="pv_corrected_from"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'pv_corrected_from';
      form.appendChild(hidden);
    }
    hidden.value = value;
  }

  _pvGetSynonyms() {
    if (!this._pvSynonymsMap) {
      this._pvSynonymsMap = pvParseSynonyms(this.dataset.pvSynonyms || '');
    }
    return this._pvSynonymsMap;
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);

    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavigationButton?.classList.toggle('is-show', !isOpen);
    }

    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

// PV: "Meinten Sie X?"-Banner auf der /search-Ergebnisseite, wenn dort über
// ?pv_corrected_from=… gelandet wurde (kommt von der Predictive-Search-Submit-Korrektur).
// Wenn nicht auf /search oder kein passender Parameter, passiert hier nichts.
(function () {
  function pvInjectSearchPageCorrectionBanner() {
    try {
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }

      // v5: Das Suchfeld der Ergebnisseite zeigt die Eingabe des Kunden, nie die interne Feldabfrage.
      const qRoh = params.get('q') || '';
      const anzeige =
        params.get('pv_anzeige') || (qRoh.indexOf('product_type:') !== -1 ? pvAnzeigeAusQuery(qRoh) : '');
      if (anzeige) {
        document.querySelectorAll('.template-search__search input[type="search"]').forEach((el) => {
          el.value = anzeige;
        });
      }

      const correctedFrom = params.get('pv_corrected_from');
      if (!correctedFrom) return;

      // Anzeige ohne Syntax; das OR der Lockerung liest sich als «oder».
      const correctedTo = anzeige || qRoh.replace(/"/g, '').replace(/\s+OR\s+/g, ' oder ');
      if (!correctedTo || correctedTo.toLowerCase() === correctedFrom.toLowerCase()) return;

      // Nur auf der Search-Ergebnisseite einblenden
      const header = document.querySelector('.template-search__header');
      if (!header) return;
      if (header.querySelector('.pv-search-correction-banner')) return;

      // Link zurueck zur Original-Query; pvfix verhindert, dass der Seitenwaechter die gewollte
      // Original-Suche gleich wieder umschreibt.
      const originalUrl = new URL(window.location.href);
      originalUrl.searchParams.delete('pv_corrected_from');
      originalUrl.searchParams.delete('pv_anzeige');
      originalUrl.searchParams.set('q', correctedFrom);
      originalUrl.searchParams.set('pvfix', '1');

      const html = pvBuildCorrectionBannerHtml(
        { original: correctedFrom, corrected: correctedTo },
        {
          actionTag: 'a',
          actionHref: originalUrl.toString(),
          actionLabel: `Stattdessen nach „${correctedFrom}“ suchen`,
        }
      );

      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const banner = wrap.firstElementChild;
      if (!banner) return;

      header.insertBefore(banner, header.firstChild);
    } catch (e) {
      // Banner ist Bonus – Fehler nie weitergeben
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pvInjectSearchPageCorrectionBanner);
  } else {
    pvInjectSearchPageCorrectionBanner();
  }
})();

// PV Paket 2 (11.10.2026): Messung der Suche (GA4 ueber dataLayer/gtag, gleiche Mechanik wie
// pv_checkliste_* in sections/pv-leadgate.liquid). Bis hierher gab es keine Zahl dazu, was gesucht,
// nicht gefunden oder angeklickt wird; das Such-Audit vom 21.08. und vom 11.10. musste alles aus
// Such-URLs nachrechnen.
//   pv_suche           Ergebnisseite geladen (je Suche einmal): suchbegriff, such_art, treffer, eingabe
//   pv_suche_leer      zusaetzlich bei 0 Treffern
//   pv_suche_klick     Treffer angeklickt: suchbegriff, position, produkt
//   pv_suche_mehr      «Mehr anzeigen»: suchbegriff, geladen
//   pv_suche_original  «Stattdessen nach … suchen»: suchbegriff
//   pv_suche_schnell   Schnellzugriff, Such-Chip oder Kontakt auf der Leerseite: suchbegriff, art, ziel
//   pv_suche_dropdown  Klick im Dropdown: suchbegriff, art, position, ziel
// Ereignisnamen ohne Ziffern. Suchbegriffe werden auf 100 Zeichen gekuerzt; Eingaben mit E-Mail-Adresse
// oder langer Ziffernfolge (Telefonnummer) werden als «(entfernt)» uebertragen.
// 13.10.2026: gesendet wird ueber window.pvGa4 (snippets/pv-ga4.liquid, nur mit Analytik-Zustimmung, ohne
// zusaetzliche page_views). Vorher dataLayer.push + window.gtag; gtag gab es nur nach dem Konfigurator,
// darum kam vom 11. bis 13.10. kein einziges pv_suche* in GA4 an.
function pvSuchMessen(ereignis, daten) {
  try {
    if (window.pvGa4) window.pvGa4.ereignis(ereignis, daten || {});
  } catch (e) {
    // Messung darf die Suche nie stoeren
  }
}

// gtag.js beim Fokus ins Suchfeld vorladen (nur mit Zustimmung, s. pvGa4), damit ein Klick im Dropdown mit
// sofortigem Seitenwechsel noch gesendet wird.
document.addEventListener(
  'focusin',
  (e) => {
    if (window.pvGa4 && e.target && e.target.closest && e.target.closest('predictive-search')) window.pvGa4.vorbereiten();
  },
  true
);

function pvSuchbegriffSauber(t) {
  const s = String(t || '').trim().slice(0, 100);
  if (/@/.test(s) || /\d[\d\s\/.-]{7,}/.test(s)) return '(entfernt)';
  return s;
}

(function () {
  if (!/\/search\b/.test(window.location.pathname)) return;

  function start() {
    const params = new URLSearchParams(window.location.search);
    const qRoh = params.get('q') || '';
    const feld = qRoh.indexOf('product_type:') !== -1;
    const korrigiert = params.get('pv_corrected_from');
    const begriff = pvSuchbegriffSauber(
      params.get('pv_anzeige') || (feld ? pvAnzeigeAusQuery(qRoh) : qRoh.replace(/"/g, ''))
    );
    const art = feld ? (korrigiert ? 'feld_umgeschrieben' : 'feld') : params.get('pvfix') ? 'roh_nachgeladen' : 'roh';
    const wurzel = document.querySelector('.template-search[data-pv-treffer]');
    const treffer = wurzel ? parseInt(wurzel.getAttribute('data-pv-treffer'), 10) : NaN;

    // 13.10.2026: Leitet die Seite gleich weiter, zaehlt erst die Zielseite (sonst eine Suche doppelt bzw. ein
    // falsches pv_suche_leer): die 0-Treffer-Kaskade in main-search.liquid leitet Feldabfragen und Anfuehrungs-
    // zeichen sofort um, der Direkteinstiegs-Waechter unten nach seinem Entscheid (window.pvSuchWaechter).
    // setTimeout, weil der Waechter erst nach diesem Block laeuft.
    const kaskade = treffer === 0 && !params.get('pvfix') && (qRoh.indexOf('product_type:') !== -1 || qRoh.indexOf('"') !== -1);
    if (qRoh && !params.get('page') && !kaskade) {
      setTimeout(() => {
        Promise.resolve(window.pvSuchWaechter).then((umgeleitet) => {
          if (umgeleitet) return;
          const schluessel = 'pv-suche-gemessen:' + window.location.search;
          let schon = false;
          try {
            schon = !!sessionStorage.getItem(schluessel);
            sessionStorage.setItem(schluessel, '1');
          } catch (e) {
            // ohne sessionStorage: lieber doppelt als gar nicht
          }
          if (!schon) {
            const daten = { suchbegriff: begriff, such_art: art, treffer: Number.isFinite(treffer) ? treffer : -1 };
            if (korrigiert) daten.eingabe = pvSuchbegriffSauber(korrigiert);
            pvSuchMessen('pv_suche', daten);
            if (treffer === 0) pvSuchMessen('pv_suche_leer', { suchbegriff: begriff, such_art: art });
          }
        });
      }, 0);
    }

    document.addEventListener(
      'click',
      (e) => {
        const a = e.target && e.target.closest ? e.target.closest('a, button') : null;
        if (!a) return;
        const karte = a.closest('#ProductsList .f-column');
        if (karte && a.tagName === 'A') {
          const position = Array.prototype.indexOf.call(karte.parentElement.children, karte) + 1;
          const href = a.getAttribute('href') || '';
          const m = href.match(/\/products\/([^?#/]+)/);
          pvSuchMessen('pv_suche_klick', { suchbegriff: begriff, position: position, produkt: m ? m[1] : href.slice(0, 80) });
          return;
        }
        if (a.matches('#ProductGridContainer .pagination button')) {
          pvSuchMessen('pv_suche_mehr', {
            suchbegriff: begriff,
            geladen: document.querySelectorAll('#ProductsList .f-column').length,
          });
          return;
        }
        if (a.classList.contains('pv-search-correction-banner__force-link')) {
          pvSuchMessen('pv_suche_original', { suchbegriff: pvSuchbegriffSauber(korrigiert || begriff) });
          return;
        }
        const schnell = a.getAttribute('data-pv-schnell') || (a.hasAttribute('data-pv-chip') ? 'chip' : '');
        if (schnell) {
          pvSuchMessen('pv_suche_schnell', {
            suchbegriff: begriff,
            art: schnell,
            ziel: (a.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60),
          });
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

// PV: Smart-Query-Wächter für Direkteinstiege auf /search?q=… (Bookmarks,
// geteilte Links, alte URLs), die nicht durchs Suchformular gelaufen sind.
// Gleiche Entscheidungslogik wie der Submit-Interceptor; leitet einmalig auf
// die Phrase-Variante um. Läuft NICHT bei: bereits korrigierten Suchen
// (pvfix/pv_corrected_from), aktiven Filtern/Pagination, Nicht-Roh-Queries
// (Anführungszeichen/Operatoren) und 0-Treffer-Seiten (dort übernimmt die
// bestehende 0-Treffer-Kaskade in main-search.liquid).
(function () {
  function pvSearchPageSmartGuard() {
    try {
      if (!/\/search\b/.test(window.location.pathname)) return;

      const params = new URLSearchParams(window.location.search);
      const q = params.get('q');
      if (!q || params.get('pvfix') || params.get('pv_corrected_from')) return;
      for (const key of params.keys()) {
        if (key.indexOf('filter.') === 0 || key === 'page') return;
      }
      if (!pvIsRawQuery(q)) return;

      const header = document.querySelector('.template-search__header');
      if (!header) return;
      // v2: Auch 0-Treffer-Seiten smart behandeln («two-way» direkt in der
      // URL). Die inline-Kaskade in main-search.liquid (Quote-Strip/Umlaut)
      // läuft zuerst; navigiert sie, ist unser späteres replace() wirkungslos.

      const el = document.querySelector('predictive-search');
      if (el && el.dataset.pvSmart === 'false') return;
      const synMap = pvParseSynonyms(el ? el.dataset.pvSynonyms : '');

      // 13.10.2026: Entscheid als Promise ablegen (true = leitet weiter); die Seitenmessung in pvSuchMessen
      // wartet darauf, damit ein Direkteinstieg nicht doppelt zaehlt.
      window.pvSuchWaechter = pvDecideSmartQuery(q, synMap).then(
        function (d) {
          if (!d || !d.query || d.query === q) return false;
          params.set('q', d.query);
          params.set('pv_anzeige', d.display || q);
          params.set('pvfix', '1');
          if (d.changed) params.set('pv_corrected_from', q);
          window.location.replace(window.location.pathname + '?' + params.toString());
          return true;
        },
        function () {
          return false;
        }
      );
    } catch (e) {
      // Wächter ist Bonus – Fehler nie weitergeben
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pvSearchPageSmartGuard);
  } else {
    pvSearchPageSmartGuard();
  }
})();
